// A component to edit a Task when entering in to the logbook. If it's
// a recurring task, updates date or counter information. Or lets user
// decide to delete the task, etc.
//
//  task = Task editing.

import { useMemo, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import { Confirm, DataCard, useStatusMessage, RadioSelection, ModalEditForm } from '../../components';
import { dateStringToDate, databaseDateTimeString, buildFieldsForItem } from '../../fleet-shared/Common';
import { dateMidnight } from '../../fleet-shared/TaskBuckets';
// The recurrence math moved to fleet-shared so TaskPogoShareTasksWrite can roll
// a task forward for a visitor completing one through a share link. See that
// module's header -- in particular the offset rule, which is why nextDueDate
// does not simply re-format through the host clock.
import {
  nextDueDate,
  nextDueCounter,
  dueDateFromNow,
  dueCounterFromNow,
} from '../../fleet-shared/TaskRecurrence.mjs';
import { taskFields } from '../../fleet-shared/TaskFields';
import { deleteTask, updateTask } from '../../utilities/Database';
import { saveQueue } from '../../utilities/SaveQueue.mjs';
import { useAutosave } from '../../utilities/useAutosave';
import { validateTask } from '../../utilities/Validators.mjs';
import { intervalSummary } from '../task/taskRowHelpers';
import TaskPogoDatePicker from '../../components/TaskPogoDatePicker';


// The counter input's text for a saved value. `0` is a real counter reading,
// so only null/undefined empties the field.
function counterText(value) {
  return (value === null || value === undefined) ? '' : String(value);
}

// Options for selectedOption
const UpdateNothing = 'nothing';
const UpdateFromDue = 'fromDue';
const UpdateFromNow = 'fromNow';
const UpdateTo      = 'to';


// Control to show update task date UI
const UpdateDate = ({ 
  task, 
  originalDate,
  selectedDateOption, 
  setSelectedDateOption,
  updateDateFromDue,
  updateDateFromNow,
  selectedDate,
  setSelectedDate
}) => {
  // User changed the date. One call: `setSelectedDate` already selects
  // "set to date" for us. (It used to also call `setSelectedDateOption`
  // straight after, which re-ran the same save with the pre-change value read
  // from a stale render.)
  function dateChanged(date) {
    var newDate = null;
    if ((date === undefined) || (date === null) || (date === "")) {
      newDate = null;
    } else {
      // Store date-only (normalized to local start-of-day); we no longer keep a time of day.
      newDate = databaseDateTimeString(dateMidnight(date));
    }

    setSelectedDate(newDate);
  }

  function buildOptions() {
    let options = [];

    options.push({
      text: `Leave task due date the same (${originalDate ? originalDate : 'no due date'}).`,
      value: UpdateNothing
    });

    if (updateDateFromDue) {
      options.push({
        text: `Set due date to ${updateDateFromDue} (${intervalSummary(task)} from due date)`,
        value: UpdateFromDue
      });
    }

    if (updateDateFromNow) {
      options.push({
        text: `Set due date to ${updateDateFromNow} (${intervalSummary(task)} from today)`,
        value: UpdateFromNow
      });
    }

    options.push({
      text: 'Set to date:',
      value: UpdateTo,
      children: 
        <div className='container d-flex align-items-center justify-content-center col-4'>
          <TaskPogoDatePicker
            selected={dateStringToDate(selectedDate())}
            onChange={dateChanged}
            dateFormat="MMM d, yyyy"
            isClearable
            placeholderText="mm/dd/yyyy"

          />
        </div>
    });

    return options;
  }

  return (
    <RadioSelection
      options={buildOptions()}
      option={selectedDateOption}
      setOption={setSelectedDateOption}
    >
    </RadioSelection>
  );
}

// Control to show update task counter UI
const UpdateCounter = ({ 
  task, 
  originalCounter,
  selectedCounterOption, 
  setSelectedCounterOption, 
  updateCounterFromDue, 
  updateCounterFromNow,
  updateCounterValue,
  setUpdateCounterValue,
  flushCounter
}) => {

  // What the user has typed. The saved value alone can't drive this input:
  // it's a controlled field, so an empty string would snap straight back to
  // the last number and the counter could never be edited by backspacing.
  // Resynced whenever the saved value moves underneath us -- a radio pick,
  // or a save landing.
  const [draft, setDraft] = useState(counterText(updateCounterValue));
  const shownValue = useRef(updateCounterValue);
  if (shownValue.current !== updateCounterValue) {
    shownValue.current = updateCounterValue;
    setDraft(counterText(updateCounterValue));
  }

  // One call, same as dateChanged above: setUpdateCounterValue selects
  // "set due counter to" itself, and the write debounces like any other typed
  // input (`onBlur` flushes it). An empty or half-typed field is mid-edit,
  // not a request to clear the counter -- there's nothing to save yet, and
  // erroring on every backspace would be nonsense. Picking the radio with the
  // field empty is where we do ask for a value (see optionPicked below).
  function counterChanged(event) {
    const text = event.target.value;
    setDraft(text);
    if (text === '') return;
    const value = parseFloat(text);
    if (Number.isNaN(value)) return;
    setUpdateCounterValue(value);
  }

  // Picking "Set due counter to" is only meaningful with a number in the box,
  // and the draft above is local -- the parent sees only the last value that
  // reached the form state. Left to fall back on that it would re-save the old
  // number while the field reads empty, silently and with no error. Say the
  // field is empty explicitly, so the parent asks for a value instead.
  function optionPicked(option) {
    if (option === UpdateTo && draft.trim() === '') {
      setSelectedCounterOption(option, null);
      return;
    }
    setSelectedCounterOption(option);
  }

  function buildOptions() {
    let options = [];

    options.push({
      text: `Leave task due counter the same (${(originalCounter > 0) ? originalCounter : 'no counter'}).`,
      value: UpdateNothing
    });

    if (updateCounterFromDue) {
      options.push({
        text: `Set task due counter to ${updateCounterFromDue} (${task.counterInterval} ticks from due counter)`,
        value: UpdateFromDue
      });
    }

    if (updateCounterFromNow) {
      options.push({
        text: `Set task due counter to ${updateCounterFromNow} (${task.counterInterval} ticks from current counter)`,
        value: UpdateFromNow
      });
    }

    options.push({
      text: 'Set due counter to:',
      value: UpdateTo,
      children: 
        <div className='container d-flex align-items-center justify-content-center col-4'>
          <input 
            name='Counter'
            type='number'
            step={1}
            placeholder='Counter'
            value={draft}
            onChange={counterChanged}
            onBlur={flushCounter}
          />

        </div>
    });


    return options;
  }

  return (
    <RadioSelection
      options={buildOptions()}
      option={selectedCounterOption}
      setOption={optionPicked}
    />
  );
}

// Why nothing is saving. The autosave controller is shared with the "Update
// Other Task Fields" modal, and an invalid task holds writes from BOTH -- but
// the per-field messages render inside that modal, which is closed whenever
// the user is looking at the radios above.
//
// Keyed by field, not by message: two fields are free to fail with the same
// wording, and `validateTask` only happens not to today.
const HeldInvalid = ({ fieldErrors }) => {
  const entries = Object.entries(fieldErrors || {});
  if (entries.length === 0) {
    return null;
  }

  return (
    <div className='text-danger small mt-2' role='alert'>
      <p className='mb-1'>
        These choices aren't being saved until the task is valid:
      </p>
      <ul className='mb-1'>
        {entries.map(([field, message]) => <li key={field}>{message}</li>)}
      </ul>
      <p className='mb-0'>Fix it under "Update Other Task Fields" below.</p>
    </div>
  );
}

export const LogbookUpdateTask = ({ task, item, onTaskDeleted }) => {
  // Constants based on the task
  const [originalDate] = useState(task.dueDate);
  const [originalCounter] = useState(task.dueCounter);
  const [updateCounterFromDue] = useState(nextDueCounter(task));
  const [updateCounterFromNow] = useState(dueCounterFromNow(task, item));
  const [updateDateFromDue] = useState(nextDueDate(task));
  const [updateDateFromNow] = useState(dueDateFromNow(task));

  // Which option the user selects for updating teh date/counter
  const [selectedDateOption, setSelectedDateOption] = useState(UpdateNothing);  //defaultDateOption(task));
  const [selectedCounterOption, setSelectedCounterOption] = useState(UpdateNothing); // defaultCounterOption(task, item));

  // Show full task editing UI
  const [showEditTask, setShowEditTask] = useState(false);

  // Confirm "Delete Task" UI. Once the task is deleted we hide the update
  // controls -- there's no longer a task to update -- but we keep the
  // surrounding logbook-entry editor on the parent page intact.
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [taskIsDeleted, setTaskIsDeleted] = useState(false);

  // For showing loading status.
  const [StatusMessage, {showText, showError, hideStatus}] = useStatusMessage();

  // Both surfaces on this page edit the same task: the radios below and the
  // "Update Other Task Fields" modal. They share ONE autosave controller
  // rather than one lane, because a lane only serializes writes -- it keeps
  // just the latest queued job, so two controllers would each build payloads
  // from their own edit session and the last one scheduled would drop the
  // other's pending edits. One controller means one session and one baseline.
  const fields = useMemo(() => buildFieldsForItem(taskFields, item), [item]);
  const autosave = useAutosave({
    recordKey: `task:${task.id}`,
    original: task,
    fields,
    validate: validateTask,
    // `updateTask` rebuilds the row from taskFields and validates the name, so
    // it wants the whole task rather than a diff of what changed.
    buildPayload: (state) => ({ ...state }),
    save: (payload) => updateTask(payload, item),
    onSaved: (saved) => {
      // The option labels ("...from due date") were derived from the `task`
      // prop at mount, and the pages above hold the same reference. Keep it
      // in step with the server's copy.
      if (saved) Object.assign(task, saved);
    },
  });
  const { formState, setFormState, fieldErrors, flush } = autosave;

  // Validation is shared too, which makes it this page's job to explain.
  // `setFormState` holds an invalid record by cancelling the payload and
  // returning -- so while the task is invalid a radio pick here saves nothing.
  // The inline errors live in `EditForm`, inside a modal that is closed most
  // of the time, so without this the radios would just quietly do nothing.
  // (Rendered by HeldInvalid below, straight from the controller.)

  // A radio pick is a discrete action -- persist it on the spot. A typed
  // counter value is text input and debounces like any other, so a
  // three-digit number is one write instead of one per keystroke. The updater
  // form reads the live edit session, so a handler that writes twice in one
  // tick can't undo itself with a stale `formState`.
  function applyDueDate(dueDate, immediate = true) {
    setFormState((current) => ({ ...current, dueDate }), { immediate });
  }

  function applyDueCounter(dueCounter, immediate = true) {
    setFormState((current) => ({ ...current, dueCounter }), { immediate });
  }

  function setSelectedDate(date) {
    dateSelectionChanged(UpdateTo, date);
  }

  function selectedDate() {
    return formState.dueDate;
  }

  function setUpdateCounterValue(value) {
    counterSelectionChanged(UpdateTo, value, { typed: true });
  }

  function updateCounterValue() {
    return formState.dueCounter;
  }

  // User changed the date selection, update the task's date and save to
  // database. `explicitDate` is the date the user just picked, when that is
  // what triggered this -- it hasn't reached `formState` yet.
  function dateSelectionChanged(selectedDateOption, explicitDate) {
    setSelectedDateOption(selectedDateOption);

    try {
      // Update the dueDate in task based on user's pick.
      switch (selectedDateOption) {
      case UpdateNothing:
        // Make original date
        applyDueDate(originalDate);
        break;
      case UpdateFromDue:
        applyDueDate(updateDateFromDue);
        break;
      case UpdateFromNow:
        applyDueDate(updateDateFromNow);
        break;
      case UpdateTo: {
        const date = explicitDate === undefined ? selectedDate() : explicitDate;
        if (null === date || undefined === date) {
          throw new Error('Please specify a date.')
        }
        applyDueDate(date);
        break;
      }
      default:
        throw new Error('Unexpected date option in saveTask');
      }
    } catch (err) {
      showError(err);
    }
  }

  // User changed the counter selection, update the task's counter and save to
  // database. `explicitValue` is the value the user just typed, for the same
  // reason as `explicitDate` above; `typed` says it came from the text field,
  // which debounces rather than writing on every keystroke.
  function counterSelectionChanged(selectedCounterOption, explicitValue, { typed = false } = {}) {
    setSelectedCounterOption(selectedCounterOption);

    try {
      // Update the due counter in task based on user's pick.
      switch (selectedCounterOption) {
      case UpdateNothing:
        // Set to original
        applyDueCounter(originalCounter);
        break;
      case UpdateFromDue:
        applyDueCounter(updateCounterFromDue);
        break;
      case UpdateFromNow:
        applyDueCounter(updateCounterFromNow);
        break;
      case UpdateTo: {
        // Was comparing the *function* `updateCounterValue` to null, so this
        // never threw -- and then assigned that function to `dueCounter`,
        // which JSON.stringify drops, so "set due counter to" silently saved
        // nothing at all.
        const value = explicitValue === undefined ? updateCounterValue() : explicitValue;
        if (null === value || undefined === value) {
          throw new Error('Please specify a counter value.')
        }
        applyDueCounter(value, !typed);
        break;
      }
      default:
        throw new Error('Unexpected counter option in saveTask');
      }
    } catch (err) {
      showError(err);
    }
  }

  function updateTaskClicked() {
    setShowEditTask(true);
  }

  function deleteTaskClicked() {
    setShowConfirmDelete(true);
  }

  // User confirmed they want to delete the underlying task. The logbook
  // entry being edited above is left alone -- only the recurring/one-off
  // task in the item's task list is removed.
  async function onConfirmDelete() {
    setShowConfirmDelete(false);
    try {
      showText('Deleting task...');

      // Cancel any pending or in-flight autosave first -- a radio pick or a
      // modal edit still in its debounce window must not land against the row
      // we're about to delete.
      saveQueue.cancel(`task:${task.id}`);

      await deleteTask(task.id, item.tasks?.items);
      hideStatus();
      setTaskIsDeleted(true);
      if (onTaskDeleted) {
        onTaskDeleted(task);
      }
    } catch (err) {
      showError(err);
    }
  }

  if (taskIsDeleted) {
    return (
      <div>
        <DataCard
          header='Task'
          body=<p className='m-0'>The task has been deleted. You can still save this logbook entry below.</p>
        />
        <StatusMessage/>
      </div>
    );
  }

  return (
    <div>
      <DataCard
        header='How would you like to update the task due date?'
        body=<UpdateDate 
          task={task} 
          originalDate={originalDate}
          selectedDateOption={selectedDateOption} 
          setSelectedDateOption={dateSelectionChanged} 
          updateDateFromDue={updateDateFromDue}
          updateDateFromNow={updateDateFromNow}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
        />
      />
      <DataCard
        header='How would you like to update the task due counter?'
        body=<UpdateCounter 
          task={task} 
          originalCounter={originalCounter}
          selectedCounterOption={selectedCounterOption} 
          setSelectedCounterOption={counterSelectionChanged} 
          updateCounterFromDue={updateCounterFromDue}
          updateCounterFromNow={updateCounterFromNow}
          updateCounterValue={updateCounterValue()}
          setUpdateCounterValue={setUpdateCounterValue}
          flushCounter={flush}
        />
      />

      <HeldInvalid fieldErrors={fieldErrors} />

      <Button className='mt-2' variant='primary' onClick={updateTaskClicked}>
          Update Other Task Fields
      </Button>
      <Button className='mt-2 ms-2' variant='danger' onClick={deleteTaskClicked}>
          Delete Task
      </Button>

      {/* Shares this page's autosave controller: the radios above edit the
          same task, and one controller is what keeps them from overwriting
          each other. See EditForm.jsx's `autosave` prop. */}
      <ModalEditForm
        title='Edit Task'
        show={showEditTask}
        onHide={() => setShowEditTask(false)}
        fields={fields}
        item={task}
        recordKeyPrefix="task"
        autosave={autosave}
      />

      <Confirm
        show={showConfirmDelete}
        onConfirm={onConfirmDelete}
        onHide={() => setShowConfirmDelete(false)}
        body='Delete this task? The logbook entry below will not be affected.'
        confirmText='Confirm Delete'
        title='Delete Task'
      />

      <StatusMessage/>
    </div>
  );
}